import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { QUIZ_QUESTIONS } from './data/questions';
import { computeQuiz } from './lib/calc';
import { track } from './lib/telemetry';
import { submitQuizLead } from './lib/api';
import { IntroScreen } from './components/introScreen';
import { QuestionScreen } from './components/questionScreen';
import { CalculatingLoader } from './components/calculatingLoader';
import { LeadGateForm } from './components/leadGateForm';
import { ResultScreen } from './components/resultScreen';
import { SolutionPage } from './components/solutionPage';
const ONBOARDING_BY_PLAN = {
    starter: '/onboarding?industry=general&src=quiz&upgrade=starter',
    advanced: '/onboarding?industry=general&src=quiz&upgrade=advanced',
};
function isComplete(a) {
    return (typeof a.workers === 'number' &&
        typeof a.wage === 'number' &&
        !!a.markup &&
        typeof a.hrsPerWeek === 'number');
}
export const QuizApp = () => {
    const [step, setStep] = useState('intro');
    const [qIdx, setQIdx] = useState(0);
    const [answers, setAnswers] = useState({});
    const [lead, setLead] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const currentQuestion = QUIZ_QUESTIONS[qIdx];
    const finalAnswers = useMemo(() => (isComplete(answers) ? answers : null), [answers]);
    const result = useMemo(() => (finalAnswers ? computeQuiz(finalAnswers) : null), [finalAnswers]);
    const handleStart = () => {
        track('quiz_started');
        setQIdx(0);
        setStep('question');
    };
    const handleSelect = (optionId) => {
        const question = currentQuestion;
        const option = question.options.find((o) => o.id === optionId);
        if (!option)
            return;
        const next = { ...answers, [question.id]: option.value };
        setAnswers(next);
        track('quiz_question_answered', { question: question.id, answer: optionId, index: qIdx + 1 });
        // Brief pause so the selection (and any insight) is visible before advancing.
        const delay = option.insight ? 750 : 280;
        window.setTimeout(() => {
            if (qIdx + 1 < QUIZ_QUESTIONS.length) {
                setQIdx(qIdx + 1);
            }
            else {
                setStep('calculating');
            }
        }, delay);
    };
    const handleBack = () => {
        if (qIdx > 0)
            setQIdx(qIdx - 1);
        else
            setStep('intro');
    };
    const handleCalculated = () => {
        if (result)
            track('quiz_calculated', { total: result.totalMarkupYear, verdict: result.verdict });
        setStep('leadGate');
        track('quiz_gate_shown');
    };
    const handleLeadSubmit = async (payload) => {
        if (!finalAnswers || !result)
            return;
        setLead(payload);
        setSubmitting(true);
        setStep('submitting');
        const res = await submitQuizLead({ lead: payload, answers: finalAnswers, verdict: result.verdict, result });
        if (res.success) {
            track('quiz_lead_submitted', { verdict: result.verdict, total: result.totalMarkupYear, id: res.id });
        }
        else {
            // Never block the user — they earned their result.
            // eslint-disable-next-line no-console
            console.warn('[quiz] lead submit failed:', res.error);
            track('quiz_lead_submit_failed', { error: res.error });
        }
        setSubmitting(false);
        setStep('result');
        track('quiz_result_viewed', { verdict: result.verdict });
    };
    const handleSeeSolution = () => {
        if (result)
            track('quiz_solution_viewed', { verdict: result.verdict });
        setStep('solution');
    };
    const goToOnboarding = () => {
        if (!result)
            return;
        track('quiz_cta_clicked', { verdict: result.verdict, plan: result.plan });
        window.location.href = ONBOARDING_BY_PLAN[result.plan];
    };
    const goToDemo = () => {
        track('quiz_demo_clicked');
        window.location.href = '/#book-demo';
    };
    const handleRetake = () => {
        setAnswers({});
        setLead(null);
        setQIdx(0);
        setStep('intro');
    };
    if (step === 'intro') {
        return _jsx(IntroScreen, { onStart: handleStart });
    }
    if (step === 'question') {
        const storedValue = answers[currentQuestion.id];
        const selectedId = currentQuestion.options.find((o) => o.value === storedValue)?.id;
        return (_jsx(QuestionScreen, { question: currentQuestion, selected: selectedId, onSelect: handleSelect, onBack: handleBack }));
    }
    if (step === 'calculating') {
        return _jsx(CalculatingLoader, { onDone: handleCalculated });
    }
    if ((step === 'leadGate' || step === 'submitting') && result) {
        return (_jsx(LeadGateForm, { teaserAmount: result.totalMarkupYear, onSubmit: handleLeadSubmit, submitting: submitting }));
    }
    if (step === 'result' && finalAnswers && result) {
        return (_jsx(ResultScreen, { answers: finalAnswers, result: result, firstName: lead?.firstName ?? '', onSeeSolution: handleSeeSolution }));
    }
    if (step === 'solution' && finalAnswers && result) {
        return (_jsx(SolutionPage, { answers: finalAnswers, result: result, firstName: lead?.firstName ?? '', onPrimaryCta: goToOnboarding, onSecondaryCta: result.verdict === 'fire' ? goToDemo : undefined, onRetake: handleRetake }));
    }
    return _jsx(IntroScreen, { onStart: handleStart });
};
